import type { ChannexPublicationResponse, ChannexUnitPublicationRow } from '@/shared/api';

export type PublicationTone = 'neutral' | 'progress' | 'ok' | 'error';

/** Der Kopf der Veröffentlichung: wo das Objekt steht und was daraus für die Apartments folgt. */
export interface PublicationSummary {
  tone: PublicationTone;
  title: string;
  text: string;
}

/** Im Verkauf ist, was der Kanalmanager bestätigt hat und nicht pausiert ist. */
export function unitsInSale(units: ChannexUnitPublicationRow[]): number {
  return units.filter((unit) => unit.state === 'Published' && unit.lifecycleStatus === 'Active')
    .length;
}

function saleText(units: ChannexUnitPublicationRow[]): string {
  const total = units.length;
  const inSale = unitsInSale(units);
  if (total === 0) return 'Diesem Objekt ist noch kein Apartment zugeordnet.';
  if (inSale === total) {
    return total === 1
      ? 'Das Apartment ist im Verkauf.'
      : `Alle ${total} Apartments sind im Verkauf.`;
  }
  if (inSale === 0) return 'Noch ist kein Apartment im Verkauf.';
  return `${inSale} von ${total} Apartments ${inSale === 1 ? 'ist' : 'sind'} im Verkauf.`;
}

/**
 * Unterwegs oder abgelehnt kann auch eine Änderung an einem Objekt sein, das beim Kanalmanager
 * längst steht — jedes Speichern am Objekt schickt eine neue Fassung. Ob das Objekt dort existiert,
 * sagt seine Kennung; ohne sie geht es um das erste Anlegen.
 */
export function publicationSummary(data: ChannexPublicationResponse): PublicationSummary {
  const exists = data.channexPropertyId !== null;
  switch (data.parentState) {
    case 'Published':
      return {
        tone: 'ok',
        title: 'Objekt ist beim Kanalmanager veröffentlicht',
        text: saleText(data.units),
      };
    case 'Publishing':
      return exists
        ? {
            tone: 'progress',
            title: 'Änderung wird übertragen',
            text: 'Das Objekt steht beim Kanalmanager; die neueste Fassung ist gerade unterwegs.',
          }
        : {
            tone: 'progress',
            title: 'Objekt wird beim Kanalmanager angelegt',
            text: 'Das Objekt entsteht gerade beim Kanalmanager. Die Apartments folgen danach.',
          };
    case 'Failed':
      return exists
        ? {
            tone: 'error',
            title: 'Letzte Änderung nicht übernommen',
            text: 'Das Objekt steht beim Kanalmanager, aber die neueste Fassung ist dort nicht angekommen.',
          }
        : {
            tone: 'error',
            title: 'Objekt konnte nicht veröffentlicht werden',
            text: 'Der Kanalmanager hat das Anlegen des Objekts abgelehnt.',
          };
    // Ein mehrdeutiger Vendor-Create darf nie per Klick wiederholt werden — der Kopf sagt, dass
    // Hilfe nötig ist, statt einen neuen Versuch anzubieten.
    case 'NeedsReconcile':
      return {
        tone: 'error',
        title: 'Veröffentlichung: Klärung nötig',
        text: 'Der Kanalmanager hat einen mehrdeutigen Zustand gemeldet. Das löst der Betreiber auf, nicht ein erneuter Versuch.',
      };
    default:
      return {
        tone: 'neutral',
        title: 'Objekt ist noch nicht beim Kanalmanager',
        text: 'Zuerst entsteht das Objekt beim Kanalmanager, danach folgen die Apartments einzeln.',
      };
  }
}
